import Big from 'big.js'
import {
  Dispatch,
  forwardRef,
  InputHTMLAttributes,
  SetStateAction,
  useEffect,
  useMemo
} from 'react'
import { RefCallBack } from 'react-hook-form'

import { uiCryptoDecimals } from '../../utils'

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
  value?: string
  initialValue?: string
  setShowDisplayValue?: Dispatch<SetStateAction<boolean>>
  setValue?: Dispatch<SetStateAction<string>>
  showDisplayValue?: boolean
}

const Input = function (
  {
    className,
    disabled = false,
    initialValue,
    onChange,
    onBlur,
    setShowDisplayValue,
    setValue,
    showDisplayValue,
    value,
    ...props
  },
  ref
) {
  const displayValue = useMemo(
    () => value && Big(value).toFixed(uiCryptoDecimals, Big.roundDown),
    [value]
  )

  // Set the input value from initialInputValue.
  // If set from initialInputValue, we show the displayValue instead
  useEffect(
    function () {
      setValue(initialValue)
      setShowDisplayValue(true)
    },
    [initialValue, setValue, setShowDisplayValue]
  )

  // emit onChange event
  useEffect(
    function () {
      onChange?.(value)
    },
    [value, onChange]
  )

  const handleChange = function (e) {
    // If the value is manually typed, we don't show the displayValue
    setShowDisplayValue(false)
    // And we prevent non number characters form being typed
    const validationRegex = /^\d*\.?\d*$/
    if (!validationRegex.test(e.target.value)) {
      return
    }
    setValue(
      e.target.value.startsWith('.') ? `0${e.target.value}` : e.target.value
    )
  }

  const handleFocus = function (e) {
    e.target.placeholder = ''
    if (showDisplayValue) {
      setValue(displayValue)
      setShowDisplayValue(false)
    }
  }

  const handleBlur = function (e) {
    e.target.placeholder = '0'
    onBlur?.()
  }

  const valueToShow = showDisplayValue ? displayValue : value

  return (
    <div className="text-gray-4 text-sm leading-6">
      <span className="text-heading3 block leading-[34px]">
        <input
          className={`${className ? className : ''} ${
            disabled ? 'cursor-not-allowed' : ''
          } w-full bg-transparent placeholder-gray-500 focus:outline-none`}
          data-sentry="operation-input"
          disabled={disabled}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder="0"
          ref={ref}
          value={valueToShow}
          {...props}
        />
      </span>
    </div>
  )
}

export default forwardRef<RefCallBack, InputProps>(Input)
